import React, { useMemo, useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Alert,
  Tooltip,
  IconButton,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Chip,
  TablePagination,
  Collapse,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import { useAgentLogs } from "../../Query/useMonitoring";
import { useQueryClient } from "@tanstack/react-query";
import { getLevelColor } from "../../Helpers/stringHelpers";
import { parseErrorLogs } from "../../Helpers/logHelpers";
import { useTranslation } from "react-i18next";

const LevelBadge: React.FC<{ level: string }> = ({ level }) => {
  const colors = getLevelColor(level);
  return (
    <Chip
      label={level}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.65rem",
        letterSpacing: "0.06em",
        backgroundColor: color.primary,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        borderRadius: "6px",
        height: 24,
      }}
    />
  );
};

const AgentLogRow: React.FC<{
  entry: {
    timestamp: string;
    level: string;
    message: string;
    traceback?: string;
  };
  idx: number;
}> = ({ entry, idx }) => {
  const [expanded, setExpanded] = useState(false);
  const hasTraceback = Boolean(entry.traceback);

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: idx % 2 === 0 ? color.primary : color.gray50,
          "&:hover": { backgroundColor: color.blue50 },
          transition: "background-color 0.15s",
          cursor: hasTraceback ? "pointer" : "default",
        }}
        onClick={() => hasTraceback && setExpanded((v) => !v)}
      >
        <TableCell
          sx={{
            fontFamily: "'Roboto Mono', monospace",
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            fontWeight: 500,
            color: color.textSecondary,
            border: "none",
            py: 1.2,
            pl: 3,

            whiteSpace: { xs: "normal", sm: "nowrap" },
          }}
        >
          {entry.timestamp}
        </TableCell>
        <TableCell align="center" sx={{ border: "none", py: 1.2 }}>
          <LevelBadge level={entry.level} />
        </TableCell>
        <TableCell
          sx={{
            border: "none",
            py: 1.2,
            pr: hasTraceback ? 0 : 3,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.6rem", sm: "0.8125rem" },
              fontFamily: "'Roboto Mono', monospace",
              color: color.textPrimary,
              wordBreak: "break-word",
            }}
          >
            {entry.message}
          </Typography>
        </TableCell>
        {hasTraceback && (
          <TableCell
            sx={{ border: "none", py: 1.2, pr: 2, width: 40 }}
            align="right"
          >
            <Tooltip
              title={expanded ? "Collapse traceback" : "Expand traceback"}
            >
              <IconButton size="small" sx={{ color: color.gray500 }}>
                {expanded ? (
                  <ExpandLessIcon sx={{ fontSize: "1.1rem" }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: "1.1rem" }} />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
        )}
        {!hasTraceback && <TableCell sx={{ border: "none", width: 40 }} />}
      </TableRow>
      {hasTraceback && (
        <TableRow
          sx={{
            backgroundColor: idx % 2 === 0 ? color.primary : color.gray50,
          }}
        >
          <TableCell colSpan={4} sx={{ border: "none", p: 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 3, pr: 3, pb: 1.5 }}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: "#1a1a2e",
                    color: "#e0e0e0",
                    fontFamily: "'Roboto Mono', monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.6,
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    border: "1px solid #2d2d4e",
                  }}
                >
                  {entry.traceback}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AgentMonitoring: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt } = useAgentLogs();
  const queryClient = useQueryClient();
  const { t } = useTranslation("monitoring");
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const entries = useMemo(() => {
    if (!data?.logs) return [];
    return parseErrorLogs(data.logs).reverse(); // newest first
  }, [data]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const matchesSearch =
        !searchTerm ||
        entry.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.timestamp.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (entry.traceback ?? "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchesLevel =
        levelFilter === "ALL" ||
        entry.level.toUpperCase() === levelFilter.toUpperCase();
      return matchesSearch && matchesLevel;
    });
  }, [entries, searchTerm, levelFilter]);

  const paginatedEntries = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEntries.slice(start, start + rowsPerPage);
  }, [filteredEntries, page, rowsPerPage]);

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          {t("agentFailedToLoadLogs", {
            message: (error as Error)?.message || t("unknownError"),
          })}
        </Alert>
      </Container>
    );
  }

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: color.primary,
        py: 4,
        px: { xs: 1, sm: 2 },
        overflowX: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 0.5,
            }}
          >
            <SmartToyIcon sx={{ fontSize: "2rem", color: color.secondary }} />
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                color: color.secondary,
                wordBreak: "break-word",
              }}
            >
              {t("agentMonitoring")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                color: color.gray500,
              }}
            >
              {t("agentAutoRefreshWithLastUpdated", { time: lastUpdated })}
            </Typography>
            <Tooltip title={t("agentRefreshNow")}>
              <IconButton
                size="small"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: ["agentLogs"],
                  })
                }
                sx={{
                  color: color.secondary,
                  "&:hover": { color: color.accent },
                }}
              >
                <RefreshIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "14px",
            backgroundColor: color.primary,
            border: `1px solid ${color.gray200}`,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder={t("agentSearchLogs")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: color.gray500, fontSize: "1.1rem" }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 300px" },
              minWidth: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
                "& input": {
                  color: color.textPrimary,
                },
                "& input::placeholder": {
                  color: color.gray500,
                  opacity: 1,
                },
              },
            }}
          />
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 140 },
              flex: { xs: "1 1 100%", sm: "0 0 140px" },
            }}
          >
            <Select
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                borderRadius: "10px",
                fontSize: { xs: "0.7rem", sm: "0.85rem" },
                color: color.textPrimary,
                "& .MuiSelect-icon": { color: color.gray500 },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.accent,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: color.white,
                    color: color.textPrimary,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "10px",
                    "& .MuiMenuItem-root": {
                      fontSize: { xs: "0.7rem", sm: "0.85rem" },
                      bgcolor: "white",
                      color: color.textPrimary,
                      "&:hover": { bgcolor: color.gray50 },
                      "&.Mui-selected": {
                        bgcolor: color.gray50,
                        "&:hover": { bgcolor: color.gray50 },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="ALL">{t("agentAllLevels")}</MenuItem>
              <MenuItem value="INFO">{t("agentInfoLevel")}</MenuItem>
              <MenuItem value="WARNING">{t("agentWarningLevel")}</MenuItem>
              <MenuItem value="ERROR">{t("agentErrorLevel")}</MenuItem>
            </Select>
          </FormControl>
          <Typography
            sx={{
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              color: color.gray500,
              ml: "auto",
            }}
          >
            {filteredEntries.length.toLocaleString()} {t("of")}{" "}
            {entries.length.toLocaleString()} {t("entries")}
          </Typography>
        </Paper>

        {/* Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            backgroundColor: color.primary,
            border: `1px solid ${color.gray200}`,
            overflow: "hidden",
          }}
        >
          <TableContainer
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: color.secondary }}>
                  <TableCell
                    sx={{
                      color: color.white,
                      fontWeight: 700,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      border: "none",
                      py: 1.5,
                      pl: 2,
                      width: { xs: "25%", sm: 200 },
                    }}
                  >
                    {t("agentTableTimestamp")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: color.white,
                      fontWeight: 700,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      border: "none",
                      py: 1.5,
                      width: { xs: "25%", sm: 200 },
                    }}
                  >
                    {t("agentTableLevel")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.white,
                      fontWeight: 700,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      border: "none",
                      py: 1.5,
                      width: { xs: "50%", sm: "auto" },
                    }}
                  >
                    {t("agentTableMessage")}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "none",
                      py: 1.5,
                      width: 40,
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEntries.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ py: 6, border: "none" }}
                    >
                      <SmartToyIcon
                        sx={{
                          fontSize: "3rem",
                          color: color.gray200,
                          mb: 1,
                        }}
                      />
                      <Typography
                        sx={{
                          color: color.gray500,
                          fontSize: "0.9rem",
                        }}
                      >
                        {t("agentNoLogEntries")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedEntries.map((entry, idx) => (
                    <AgentLogRow
                      key={`${entry.timestamp}-${idx}`}
                      entry={entry}
                      idx={idx}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredEntries.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            sx={{
              borderTop: `1px solid ${color.gray200}`,
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.8rem",
                  color: color.gray500,
                },
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default AgentMonitoring;
